<template>
  <button
    :type="type"
    :class="buttonClasses"
    :style="buttonStyles"
    :disabled="disabled"
    @click="$emit('click', $event)"
    @mouseenter="$emit('mouseenter', $event)"
    @mouseleave="$emit('mouseleave', $event)"
  >
    <span v-if="$slots.default" class="button-content">
      <slot></slot>
    </span>
    <span v-else>{{ label }}</span>
  </button>
</template>

<script>
export default {
  name: 'AppButton',
  
  props: {
    // Основные пропсы
    label: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'button', // button, submit, reset
      validator: (value) => ['button', 'submit', 'reset'].includes(value)
    },
    variant: {
      type: String,
      default: 'primary', // primary, secondary, success, danger, warning, info
      validator: (value) => ['primary', 'secondary', 'success', 'danger', 'warning', 'info'].includes(value)
    },
    size: {
      type: String,
      default: 'medium', // small, medium, large
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Стили
    className: {
      type: String,
      default: ''
    },
    width: {
      type: String,
      default: ''
    },
    height: {
      type: String,
      default: ''
    },
    backgroundColor: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: ''
    },
    // Дополнительные опции
    outlined: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: true
    },
    block: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['click', 'mouseenter', 'mouseleave'],
  
  computed: {
    buttonClasses() {
      return [
        'app-button',
        `app-button--${this.variant}`,
        `app-button--${this.size}`,
        {
          'app-button--disabled': this.disabled,
          'app-button--outlined': this.outlined,
          'app-button--rounded': this.rounded,
          'app-button--block': this.block,
          'app-button--loading': this.loading
        },
        this.className
      ]
    },
    
    buttonStyles() {
      const styles = {}
      if (this.width) styles.width = this.width
      if (this.height) styles.height = this.height
      if (this.backgroundColor) styles.backgroundColor = this.backgroundColor
      if (this.color) styles.color = this.color
      return styles
    }
  }
}
</script>

<style scoped>
.app-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 400;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  font-size: 16px;
  line-height: 120%;
  box-shadow: -5px -3px 8px 0 #fff, 3px 3px 4px 0 rgba(0, 0, 0, 0.1);
}

/* Размеры */
.app-button--small {
  padding: 8px 16px;
  font-size: 12px;
  min-height: 32px;
}

.app-button--medium {
  padding: 10px 20px;
  font-size: 14px;
  min-height: 40px;
}

.app-button--large {
  padding: 16px 32px;
  font-size: 16px;
  min-height: 48px;
}

/* Варианты */
.app-button--primary {
  background-color: #496782;
  color: white;
}

.app-button--secondary {
  background-color: #6c757d;
  color: white;
}

.app-button--success {
  background-color: #28a745;
  color: white;
}

.app-button--danger {
  background-color: #dc3545;
  color: white;
}

.app-button--warning {
  background-color: #ffc107;
  color: #212529;
}

.app-button--grey {
  background-color: #999;
  color: #fff;
}

.app-button--info {
  background-color: #17a2b8;
  color: white;
}

/* Outlined вариант */
.app-button--outlined {
  background-color: transparent;
  border: 2px solid;
}

.app-button--outlined.app-button--primary {
  border-color: #007bff;
  color: #007bff;
}

.app-button--outlined.app-button--secondary {
  border-color: #6c757d;
  color: #6c757d;
}

.app-button--outlined.app-button--success {
  border-color: #28a745;
  color: #28a745;
}

.app-button--outlined.app-button--danger {
  border-color: #dc3545;
  color: #dc3545;
}

.app-button--outlined.app-button--warning {
  border-color: #ffc107;
  color: #ffc107;
}

.app-button--outlined.app-button--info {
  border-color: #17a2b8;
  color: #17a2b8;
}

/* Состояния */
.app-button--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.app-button--rounded {
  border-radius: 10px;
}

.app-button--block {
  width: 100%;
  display: flex;
}

.app-button--loading {
  pointer-events: none;
  opacity: 0.8;
}

/* Ховер эффекты */
.app-button:not(.app-button--disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.app-button--outlined:not(.app-button--disabled):hover {
  background-color: currentColor;
  color: white;
}

.button-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* Мобильные стили */
@media (max-width: 768px) {
  .app-button--medium {
    padding: 10px 16px;
    font-size: 13px;
    min-height: 38px;
  }
  
  .app-button--large {
    padding: 12px 20px;
    font-size: 14px;
    min-height: 42px;
  }
}

@media (max-width: 480px) {
  .app-button--medium {
    padding: 8px 14px;
    font-size: 12px;
    min-height: 36px;
  }
}
</style>